@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin.menu

import androidx.compose.foundation.Image
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.toComposeImageBitmap
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import org.jetbrains.skia.Image as SkiaImage
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.Contextual
import kotlinx.serialization.Serializable
import us.laantech.common.ui.DataTableView
import us.laantech.common.ui.DropdownMenu
import us.laantech.common.util.tryFormatAsDateTime
import us.laantech.common.api.KtorClient
import kotlin.time.Clock

@Serializable
data class PosError(
    @Contextual val _id: String? = null,
    val createdAt: Long = Clock.System.now().toEpochMilliseconds(),
    val tag: String? = null,
    val message: String? = null,
    val stackTrace: List<String> = emptyList(),
    val imageBase64: String? = null,
)

data class LogsViewerUiState(
    val logs: List<PosError> = emptyList(),
    val selectedLog: PosError? = null,
    val showLogDetailModal: Boolean = false,

    val tags: List<String> = emptyList(),
    val selectedTag: String? = null,

    val page: Int = 0,
    val pageSize: Int = 10,
    val hasNextPage: Boolean = false,
)

@Composable
fun LogsViewer(
    viewModel: LogsViewerViewModel = viewModel(
        factory = viewModelFactory {
            initializer { LogsViewerViewModel() }
        }
    )
) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    LaunchedEffect(Unit) {
        viewModel.loadTags()
        viewModel.loadLogs()
    }

    Column(modifier = Modifier.fillMaxSize().padding(8.dp)) {

        Button(onClick = {
            CoroutineScope(Dispatchers.Default).launch {
                viewModel.loadTags()
                viewModel.loadLogs()
            }
        }) { Text("Refresh") }

        DropdownMenu(
            label = "Select Tag",
            items = uiState.tags,
            selectedItem = uiState.selectedTag,
            itemLabel = { tag -> tag },
            onItemSelected = { viewModel.selectTag(it) },
            modifier = Modifier,
            noneLabel = "No Tag Selected"
        )


        DataTableView(
            rows = uiState.logs,
            modifier = Modifier.weight(1f).padding(8.dp),
        ) {

            column("createdAt", weight = 1f) {
                Text("${it.createdAt.tryFormatAsDateTime()}", modifier = Modifier.weight(1f))
            }
            column("tag", weight = 1f) {
                Text("${it.tag}", modifier = Modifier.weight(1f))
            }
            column("message", weight = 5f) {
                Text("${it.message}", modifier = Modifier.weight(3f))
            }

            column("action", weight = 1f) {
                Button({viewModel.toggleLogDetailModal(it)}, modifier = Modifier.weight(1f)) {
                    Text("View")
                }
            }

        }

        Row(
            modifier = Modifier.fillMaxWidth().padding(8.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = androidx.compose.ui.Alignment.CenterVertically,
        ) {
            Button(
                onClick = { viewModel.prevPage() },
                enabled = uiState.page > 0,
            ) { Text("Previous") }

            Text("Page ${uiState.page + 1}")

            Button(
                onClick = { viewModel.nextPage() },
                enabled = uiState.hasNextPage,
            ) { Text("Next") }
        }
    }

    if (uiState.showLogDetailModal) {
        // Use the low-level Dialog rather than BasicAlertDialog: BasicAlertDialog clamps
        // its content to a 560.dp max width internally, which can't be overridden.
        Dialog(
            onDismissRequest = { viewModel.toggleLogDetailModal(null) },
            properties = DialogProperties(usePlatformDefaultWidth = false),
        ) {
            Surface(modifier = Modifier.fillMaxWidth(0.9f), shape = MaterialTheme.shapes.medium) {
                SelectionContainer {

                    val clipboardManager = LocalClipboardManager.current

                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(16.dp)
                            .verticalScroll(rememberScrollState())
                    ) {
                        uiState.selectedLog ?: Text("Loading")

                        Text(uiState.selectedLog?.createdAt?.tryFormatAsDateTime() ?: "Loading", modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center)
                        Text(uiState.selectedLog?.tag ?: "Loading", modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center)
                        Text(uiState.selectedLog?.message ?: "Loading", modifier = Modifier.fillMaxWidth(), textAlign = TextAlign.Center)

                        Row(
                            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = androidx.compose.ui.Alignment.CenterVertically,
                        ) {
                            Text("Stack Trace:")
                            val stackTrace = uiState.selectedLog?.stackTrace
                            if (!stackTrace.isNullOrEmpty()) {
                                Button(onClick = {
                                    clipboardManager.setText(AnnotatedString(stackTrace.joinToString("\n")))
                                }) { Text("Copy") }
                            }
                        }
                        uiState.selectedLog?.stackTrace?.forEach {
                            Text(it, modifier = Modifier.padding(top = 8.dp))
                        }

                        uiState.selectedLog?.imageBase64?.let { imageBase64 ->
                            @OptIn(ExperimentalEncodingApi::class)
                            val bitmap = remember(imageBase64) {
                                runCatching {
                                    val raw = if (imageBase64.contains(",")) imageBase64.substringAfter(",") else imageBase64
                                    SkiaImage.makeFromEncoded(Base64.decode(raw)).toComposeImageBitmap()
                                }.getOrNull()
                            }
                            bitmap?.let {
                                Image(
                                    bitmap = it,
                                    contentDescription = "Error Image",
                                    modifier = Modifier
                                        .fillMaxWidth()
//                                        .height(256.dp)
                                        .padding(4.dp)
                                )
                            }
                        }

                    }
                }
            }
        }
    }
}

class LogsViewerViewModel: ViewModel() {

    private val _uiState = MutableStateFlow(LogsViewerUiState())
    val uiState = _uiState.asStateFlow()
    val ktClient = KtorClient.instance.client

    suspend fun loadLogs() {
        val response = ktClient.get("/admin/diagnostics/logs") {
            parameter("tag", _uiState.value.selectedTag)
            parameter("page", _uiState.value.page)
            parameter("page_size", _uiState.value.pageSize)
        }
        val logs = response.body<List<PosError>>()
        _uiState.update { it.copy(logs = logs, hasNextPage = logs.size == it.pageSize) }
    }

    fun nextPage() {
        if (!_uiState.value.hasNextPage) return
        _uiState.update { it.copy(page = it.page + 1) }
        CoroutineScope(Dispatchers.Default).launch {
            loadLogs()
        }
    }

    fun prevPage() {
        if (_uiState.value.page <= 0) return
        _uiState.update { it.copy(page = it.page - 1) }
        CoroutineScope(Dispatchers.Default).launch {
            loadLogs()
        }
    }

    suspend fun loadTags() {
        val response = ktClient.get("/admin/diagnostics/logs/tags")
        val tags = response.body<List<String>>()
        _uiState.update { it.copy(tags = tags) }
    }


    fun toggleLogDetailModal(log: PosError?) {

        // get complete log from server
        if (log != null) {
            CoroutineScope(Dispatchers.Default).launch {
                _uiState.update { it.copy(selectedLog = null) }

                val response = ktClient.get("/admin/diagnostics/logs/${log._id}")
                val completeLog = response.body<PosError>()

                _uiState.update { it.copy(selectedLog = completeLog) }
            }
        }

        _uiState.update { it.copy(showLogDetailModal = !it.showLogDetailModal) }
    }

    fun selectTag(tag: String?) {
        _uiState.update { it.copy(selectedTag = tag, page = 0) }
        CoroutineScope(Dispatchers.Default).launch {
            loadLogs()
        }
    }
}